import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  StatefulService
} from '@core/services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import { UserService } from '@core/user.service';
import { IActivityData } from '@models/activity/activityData';
import { IAssessment, IObjective } from '@models/admin-benchmark';
import { Classroom } from '@models/classroom';
import { IClassroomProductDescription } from '@models/classroom-products';
import { KeyValuePair } from '@models/key-value-pair';
import { SchoolLicense } from '@models/license/school-license';
import { School } from '@models/school';
import {
  BenchmarkReportTypeModel,
  BenchmarkReportTypes,
  BenchmarkQueryParamsByType
} from '@shared/enums/benchmark-report-types';
import { Capabilities } from '@shared/enums/capabilities';
import { RoleType } from '@shared/enums/role-type';
import { BehaviorSubject } from 'rxjs';

/**
 * Used for configurations, form control names, and data to hydrate shared dropdowns
 */
@Injectable()
export class BenchmarkReportsStateService implements StatefulService {
  isDistrictAdmin = this.userService.user?.isViewingAsRole(RoleType.DistrictAdministrator);
  districtId?: string;

  // These Behavior Subjects are used for hydrating dropdowns.
  // The report component should be subscribed to and updated by the specific report
  // whenever the values are updated from the API in the report,
  // allowing the dropdowns to be hydrated when switching report types.
  // The QueryParamService binds the selected value to the control,
  // allowing the selection states to stay the same when switching report types.
  schools$ = new BehaviorSubject<School[]>(null);
  /** These are all products for the selected educationalUnitId, not filtered out by Capability*/
  /** Heatmap uses IClassroomProductDescription and Class Comparison uses SchoolLicense*/
  products$ = new BehaviorSubject<SchoolLicense[] | IClassroomProductDescription[]>(null);
  assessments$ = new BehaviorSubject<IAssessment[]>(null);
  /** Activity Data is returned by the API when selecting a School in the dropdown */
  activityData$ = new BehaviorSubject<IActivityData>(null);
  schoolYears$ = new BehaviorSubject<KeyValuePair[]>(null);
  /** Classrooms returned from API */
  classrooms$ = new BehaviorSubject<Classroom[]>(null);
  skills$ = new BehaviorSubject<IObjective[]>(null);


  /** BenchmarkReportsComponent sets this to false on load.
   *
   * Do not do an automatic search if this has been set to true
   *
   * Temporarily setting this to 'true' by default while School and District Admin Modules are not deep-linkable.
   * Turning this on initiates the initial search from the query params when the form is valid.
   */
  queryParamsInitiallySetandRan: boolean = true;

  /**
   * Report Control is watched by Reports Parent Container to know when to switch between reports components
   *
   *  Instantiated by {@link BrandedBenchmarkReportsComponent}
   */
  reportControl: FormControl<BenchmarkQueryParamsByType>;

  // Configure available report types
  // Configuration allows text of the report type displayed in the dropdown to change
  // without impacting any deep links for that report type.

  heatmapReportTypeConfig: BenchmarkReportTypeModel = {
    type: BenchmarkReportTypes.Heatmap,
    queryParam: BenchmarkQueryParamsByType.Heatmap,
    capability: Capabilities.HeatmapReport,
  };

  classComparisonReportTypeConfig: BenchmarkReportTypeModel = {
    type: BenchmarkReportTypes.ClassComparison,
    queryParam: BenchmarkQueryParamsByType.ClassComparison,
    capability: Capabilities.ClassComparisonReport,
  };

  standardsOverTimeReportTypeConfig: BenchmarkReportTypeModel = {
    type: BenchmarkReportTypes.StandardsOverTime,
    queryParam: BenchmarkQueryParamsByType.StandardsOverTime,
    capability: Capabilities.StandardsOverTimeReport,
  };

  standardsDetailReportTypeConfig: BenchmarkReportTypeModel = {
    type: BenchmarkReportTypes.StandardsDetail,
    queryParam: BenchmarkQueryParamsByType.StandardsDetail,
    capability: Capabilities.StandardsDetailReport,
  };

  skillAverageReportTypeConfig: BenchmarkReportTypeModel = {
    type: BenchmarkReportTypes.SkillAverage,
    queryParam: BenchmarkQueryParamsByType.SkillAverage,
    capability: Capabilities.SkillsAverageReport,
  };


  /** All available report configurations */
  readonly reportTypeConfigs = [
    this.classComparisonReportTypeConfig,
    this.heatmapReportTypeConfig,
    this.standardsOverTimeReportTypeConfig,
    this.standardsDetailReportTypeConfig,
    this.skillAverageReportTypeConfig,
  ];

  /**
   * Form Control names should be consistent between Benchmark Report Types
   * for the QueryParamService to integrate properly
   */
  readonly formControlNames = {
    schoolId: 'schoolId',
    schoolYear: 'schoolYear',
    report: 'report',
    classroomId: 'classroomId',
    productSkuId: 'productSkuId',
    assessmentId: 'assessmentId',
    grade: 'grade',
    skill: 'skill',
  };

  readonly districtWideSchool = {
    educationalUnitId: 'district-wide',
    name: 'District Wide'
  } as School;

  readonly allClassesOption = {
    classroomId: 'all-classes',
    name: 'All Classes',
    classroomNameYear: 'All Classes',
    licenses: [],
  } as Classroom;

  readonly allTestsAssessment = {
    assessmentKey: 'all-tests',
    assessmentName: 'All Tests',
  } as IAssessment;

  constructor(
    private userService: UserService,
  ) {
  }

  reset() {
    this.isDistrictAdmin = false;
    this.districtId = null;
    this.schools$.next(null);
    this.products$.next(null);
    this.products$.next(null);
    this.assessments$.next(null);
    this.activityData$.next(null);
    this.schoolYears$.next(null);
    this.classrooms$.next(null);
    this.skills$.next(null);
  }
}
